import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "@i18n";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import CheckBoxComponent from "@CubeComponents/Mui_Form/CheckBox";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import UserNameTagComponent from "../../../cubeComponent/html/UserNameTagComponent";
import IconComponent from "@CubeComponents/icons";
import { toObjectIdString } from "@Helpers/schemas/common.schema";
import { usePortalChat } from "./context";
import { VirtualizedList } from "./VirtualizedList";
import { useChatDirectory } from "../hooks/useChatDirectory";
import { isImageAttachment } from "../core/chatThreadModel";
import {
  PICKER_ROW_HEIGHT,
  ATTACHMENT_ROW_HEIGHT,
  GROUP_NAME_MAX_LENGTH,
  TOPIC_MAX_LENGTH,
  SEARCH_MAX_LENGTH,
} from "../portalChat.constants";
import {
  addMembersRequest,
  removeMemberRequest,
  renameConversationRequest,
  getConversationAttachmentsRequest,
  type ChatConversation,
  type ChatDirectoryUser,
  type ChatAttachmentItem,
} from "../api/portalChat.api";

const FIELD_CLASS =
  "h-10 w-full rounded-lg border border-[#DDE3E8] px-3 text-[0.84375rem] text-[#1C2228] outline-hidden focus:border-[#4794BA]";
const LABEL_CLASS = "mb-1.5 block text-xs font-semibold text-[#4A545C]";

/** Members list for group/space chats; add (any member) and remove (effective owner) for groups. */
export function MembersDialog({
  conversation,
  onClose,
}: {
  readonly conversation: ChatConversation;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation();
  const chat = usePortalChat();
  const threadMembers = chat.thread?.members;
  const members = useMemo(() => threadMembers ?? [], [threadMembers]);
  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<ChatDirectoryUser[]>([]);
  const [busy, setBusy] = useState(false);

  const isGroup = conversation.type === "group";
  const isOwner = String(conversation.createdBy) === chat.userId;

  // Server-side search + pagination, fetched only while the add flow is open.
  const { users: directory, loadMore } = useChatDirectory(search, adding);
  const candidates = useMemo(() => {
    const memberIds = new Set(members.map((member) => String(member._id)));
    return directory.filter((user) => !memberIds.has(String(user._id)));
  }, [directory, members]);

  const submitAdd = async () => {
    if (!selected.length || busy) return;
    setBusy(true);
    try {
      const response = await addMembersRequest({
        conversationId: toObjectIdString(conversation._id),
        memberIds: selected.map((user) => toObjectIdString(String(user._id))),
      });
      if (response.data.status) {
        chat.showToast(t("Members added"));
        setAdding(false);
        setSelected([]);
        await chat.refreshList();
      } else {
        chat.showServerError(response.data.errorMessage, t("Could not add members"));
      }
    } finally {
      setBusy(false);
    }
  };

  const remove = async (userId: string) => {
    if (busy) return;
    setBusy(true);
    try {
      const response = await removeMemberRequest({
        conversationId: toObjectIdString(conversation._id),
        userId: toObjectIdString(userId),
      });
      if (response.data.status) {
        chat.showToast(t("Member removed"));
        await chat.refreshList();
      } else {
        chat.showServerError(response.data.errorMessage, t("Could not remove the member"));
      }
    } finally {
      setBusy(false);
    }
  };

  const toggleCandidate = (user: ChatDirectoryUser) =>
    setSelected((prev) =>
      prev.some((u) => String(u._id) === String(user._id))
        ? prev.filter((u) => String(u._id) !== String(user._id))
        : [...prev, user],
    );

  return (
    <ModalStateDialogComponent
      isVisible
      width="29rem"
      headerTitle={
        <span className="flex flex-col">
          <span>{t("Members")}</span>
          <span className="text-xs font-normal text-[#6B757D]">
            {conversation.name} · {t("{{count}} people", { count: members.length })}
          </span>
        </span>
      }
      onCancel={onClose}
      footerContent={<></>}
      modelContent={
        <div className="flex w-full flex-col gap-2.5">
          {conversation.type === "space" && (
            <div className="rounded-md bg-[#F4FAFD] px-2.5 py-1.5 text-xs text-[#6B757D]">
              {t("Membership follows the Space — manage members from the Space settings.")}
            </div>
          )}
          {isGroup && !adding && (
            <div>
              <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={() => setAdding(true)}>
                {t("Add members")}
              </ButtonComponent>
            </div>
          )}
          {isGroup && adding && (
            <div className="flex flex-col gap-2 rounded-lg border border-[#EEF2F5] p-2.5">
              <input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                maxLength={SEARCH_MAX_LENGTH}
                placeholder={t("Search by name or email")}
                className={FIELD_CLASS}
              />
              <div className="h-40">
                {candidates.length > 0 && (
                  <VirtualizedList
                    items={candidates}
                    getKey={(user) => String(user._id)}
                    rowHeight={PICKER_ROW_HEIGHT}
                    onEndReached={loadMore}
                    ariaLabel={t("Search by name or email")}
                    renderRow={(user) => {
                      const isSelected = selected.some((u) => String(u._id) === String(user._id));
                      return (
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={() => toggleCandidate(user)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") toggleCandidate(user);
                          }}
                          className={`flex h-full w-full cursor-pointer items-center gap-2 rounded-lg px-1 text-start text-sm hover:bg-[#F7F9FB] ${
                            isSelected ? "bg-[#F4FAFD]" : ""
                          }`}
                        >
                          <span
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                            role="presentation"
                          >
                            <CheckBoxComponent checked={isSelected} onChange={() => toggleCandidate(user)} />
                          </span>
                          <UserNameTagComponent showAvatarOnly id={String(user._id)} size="XS" isCircle />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate font-semibold text-[#1C2228]">{user.fullName}</span>
                            <span className="block truncate text-[0.7rem] text-[#6B757D]">
                              {user.designation ?? user.email}
                            </span>
                          </span>
                        </div>
                      );
                    }}
                  />
                )}
                {!candidates.length && (
                  <div className="p-2 text-xs text-[#9AA5AD]">{t("No members match your search.")}</div>
                )}
              </div>
              <div className="flex gap-2.5">
                <ButtonComponent
                  buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
                  disabled={!selected.length || busy}
                  onClick={() => void submitAdd()}
                >
                  {t("Add")}
                </ButtonComponent>
                <ButtonComponent
                  buttonType={BUTTON_TYPES.BORDER_BUTTON}
                  onClick={() => {
                    setAdding(false);
                    setSelected([]);
                  }}
                >
                  {t("Cancel")}
                </ButtonComponent>
              </div>
            </div>
          )}
          <div className="h-[40vh]">
            <VirtualizedList
              items={members}
              getKey={(member) => String(member._id)}
              rowHeight={PICKER_ROW_HEIGHT}
              ariaLabel={t("Members")}
              renderRow={(member) => (
                <div className="flex h-full items-center justify-between gap-2 border-b border-[#EEF2F5] px-1">
                  <span className="flex min-w-0 items-center gap-2.5 text-sm">
                    <UserNameTagComponent showAvatarOnly id={String(member._id)} size="XS" isCircle />
                    <span className="min-w-0">
                      <span className="flex items-center gap-1.5">
                        <span className="truncate font-semibold text-[#1C2228]">
                          {String(member._id) === chat.userId ? `${member.fullName} (${t("You")})` : member.fullName}
                        </span>
                        {isGroup && String(member._id) === String(conversation.createdBy) && (
                          <span className="rounded-full bg-[#E4F1F9] px-1.5 text-[0.6rem] font-bold text-[#1F5F80]">
                            {t("Owner")}
                          </span>
                        )}
                      </span>
                      {member.designation && (
                        <span className="block truncate text-[0.7rem] text-[#6B757D]">{member.designation}</span>
                      )}
                    </span>
                  </span>
                  {isGroup && isOwner && String(member._id) !== chat.userId && (
                    <IconComponent
                      name="DeleteIcon"
                      width="0.9rem"
                      height="0.9rem"
                      toolTipContent={t("Remove")}
                      onClick={() => void remove(String(member._id))}
                    />
                  )}
                </div>
              )}
            />
          </div>
        </div>
      }
    />
  );
}

/**
 * View-attachments gallery (PRD): a data grid — select-all / per-row checkboxes, name, shared by,
 * date (newest/oldest toggle on the header) — with search and a multi-select Download.
 */
export function AttachmentsDialog({
  conversation,
  onClose,
}: {
  readonly conversation: ChatConversation;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation();
  const [items, setItems] = useState<ChatAttachmentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [newestFirst, setNewestFirst] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    // Fire-and-forget: an effect callback cannot be async; the chain guards on `cancelled` and
    // clears its own loader.
    void getConversationAttachmentsRequest({ conversationId: toObjectIdString(conversation._id) })
      .then((response) => {
        if (!cancelled && response.data.status) setItems(response.data.result ?? []);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [conversation._id]);

  const visible = useMemo(() => {
    const lower = search.trim().toLowerCase();
    const filtered = items.filter((item) => !lower || item.attachmentsData.name.toLowerCase().includes(lower));
    return newestFirst ? filtered : [...filtered].reverse();
  }, [items, search, newestFirst]);

  const allSelected = visible.length > 0 && visible.every((item) => selected.has(item.awsKey));

  const toggle = (key: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(visible.map((item) => item.awsKey)));

  const downloadSelected = () => {
    for (const item of visible) {
      if (selected.has(item.awsKey) && item.downloadLink) window.open(item.downloadLink, "_blank");
    }
  };

  const headerCell = "text-[0.65rem] font-bold uppercase tracking-[0.08em] text-[#6B757D]";

  return (
    <ModalStateDialogComponent
      isVisible
      width="36rem"
      headerTitle={t("Attachments")}
      onCancel={onClose}
      modelContent={
        <div className="flex w-full flex-col gap-2.5">
          <div className="relative">
            <span className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2">
              <IconComponent name="SearchIcon" width="0.9rem" height="0.9rem" />
            </span>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              maxLength={SEARCH_MAX_LENGTH}
              placeholder={t("Search attachments")}
              className={`${FIELD_CLASS} ps-9`}
            />
          </div>
          <div className="flex h-[26rem] flex-col overflow-hidden rounded-lg border border-[#DDE3E8]">
            <div className="flex items-center gap-2 border-b border-[#DDE3E8] bg-[#F7F9FB] px-2 py-1">
              <span className="flex w-9 items-center">
                <CheckBoxComponent checked={allSelected} disabled={!visible.length} onChange={toggleAll} />
              </span>
              <span className={`flex-1 ${headerCell}`}>{t("Name")}</span>
              <span className={`w-32 ${headerCell}`}>{t("Shared by")}</span>
              <button
                type="button"
                className={`flex w-28 items-center gap-1 text-start ${headerCell}`}
                title={newestFirst ? t("Newest first") : t("Oldest first")}
                onClick={() => setNewestFirst((prev) => !prev)}
              >
                {t("Date")}
                <IconComponent
                  name="BottomArrow"
                  width="0.55rem"
                  height="0.55rem"
                  className={newestFirst ? "" : "rotate-180"}
                />
              </button>
            </div>
            <div className="min-h-0 flex-1">
              {loading && <div className="p-3 text-sm text-[#9AA5AD]">{t("Loading")}…</div>}
              {!loading && !items.length && (
                <div className="flex h-full flex-col items-center justify-center gap-1 p-4 text-center">
                  <span className="text-sm font-bold text-[#1C2228]">{t("No attachments yet")}</span>
                  <span className="text-xs text-[#6B757D]">
                    {t("Files and photos shared in this conversation will appear here.")}
                  </span>
                </div>
              )}
              {!loading && items.length > 0 && !visible.length && (
                <div className="p-3 text-sm text-[#9AA5AD]">{t("No attachments match your search.")}</div>
              )}
              {visible.length > 0 && (
                <VirtualizedList
                  items={visible}
                  getKey={(item) => item.awsKey}
                  rowHeight={ATTACHMENT_ROW_HEIGHT}
                  ariaLabel={t("Attachments")}
                  renderRow={(item) => (
                    <div
                      className={`flex h-full items-center gap-2 border-b border-[#EEF2F5] px-2 ${
                        selected.has(item.awsKey) ? "bg-[#F4FAFD]" : "hover:bg-[#F7F9FB]"
                      }`}
                    >
                      <span className="flex w-9 items-center">
                        <CheckBoxComponent checked={selected.has(item.awsKey)} onChange={() => toggle(item.awsKey)} />
                      </span>
                      <span className="flex min-w-0 flex-1 items-center gap-2">
                        <IconComponent
                          name={isImageAttachment(item.attachmentsData.type) ? "image" : "attachFile"}
                          width="0.9rem"
                          height="0.9rem"
                        />
                        <a
                          href={item.downloadLink}
                          target="_blank"
                          rel="noreferrer"
                          className="min-w-0 flex-1 truncate text-[0.8125rem] font-semibold text-[#1F5F80] hover:underline"
                        >
                          {item.attachmentsData.name}
                        </a>
                      </span>
                      <span className="w-32 truncate text-xs text-[#4A545C]">{item.senderName}</span>
                      <span className="w-28 whitespace-nowrap text-xs text-[#6B757D]">
                        {new Date(item.createdDate).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                />
              )}
            </div>
          </div>
        </div>
      }
      footerContent={
        <div className="flex w-full items-center justify-between gap-3">
          <span className="text-xs text-[#6B757D]">
            {selected.size
              ? t("{{selected}} of {{total}} selected", { selected: selected.size, total: visible.length })
              : t("{{total}} attachments", { total: visible.length })}
          </span>
          <ButtonComponent
            buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
            disabled={!selected.size}
            onClick={downloadSelected}
          >
            {t("Download")}
          </ButtonComponent>
        </div>
      }
    />
  );
}

/** Rename a group chat: name required, topic optional (PRD). */
export function RenameDialog({
  conversation,
  onClose,
}: {
  readonly conversation: ChatConversation;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation();
  const chat = usePortalChat();
  const [name, setName] = useState(conversation.name);
  const [topic, setTopic] = useState(conversation.topic ?? "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!name.trim() || busy) return;
    setBusy(true);
    try {
      const response = await renameConversationRequest({
        conversationId: toObjectIdString(conversation._id),
        name: name.trim(),
        topic: topic.trim(),
      });
      if (response.data.status) {
        chat.showToast(t("Conversation renamed."));
        await chat.refreshList();
        onClose();
      } else {
        chat.showServerError(response.data.errorMessage, t("Could not rename the chat"));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalStateDialogComponent
      isVisible
      width="25rem"
      headerTitle={t("Name this conversation")}
      onCancel={onClose}
      modelContent={
        <div className="flex w-full flex-col gap-3">
          <div className="text-xs text-[#6B757D]">{t("Everyone in the group sees the new name.")}</div>
          <label className="block">
            <span className={LABEL_CLASS}>{t("Conversation name")}</span>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={GROUP_NAME_MAX_LENGTH}
              className={FIELD_CLASS}
            />
          </label>
          <label className="block">
            <span className={LABEL_CLASS}>
              {t("Topic")} <span className="font-normal text-[#9AA5AD]">{t("— optional, shown under the name")}</span>
            </span>
            <input
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              maxLength={TOPIC_MAX_LENGTH}
              placeholder={t("e.g. Rebar clash at the east grid")}
              className={FIELD_CLASS}
            />
          </label>
        </div>
      }
      footerContent={
        <div className="flex gap-2.5">
          <ButtonComponent
            buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
            disabled={!name.trim() || busy}
            onClick={() => void save()}
          >
            {t("Save")}
          </ButtonComponent>
          <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={onClose}>
            {t("Cancel")}
          </ButtonComponent>
        </div>
      }
    />
  );
}
